import { Component, Input, Output, EventEmitter } from '@angular/core';
import { stemsEQModel } from 'src/app/models/magic-search.model';
import {
  MAGIC_SEARCH_STEM_IDS,
  StemId,
  EQ_MIN,
  EQ_MAX,
  EQ_PRESETS,
  DEFAULT_STEMS,
} from 'src/app/constants/magic-search.constants';

const STEM_ICONS: Record<StemId, string> = {
  drums: 'pi-wave-pulse',
  bass:  'pi-volume-up',
  melody:'pi-music',
  vocals:'pi-microphone',
  guitar:'pi-star',
  keys:  'pi-table',
};

@Component({
  selector: 'ipm-magic-search-stem-eq',
  templateUrl: './magic-search-stem-eq.component.html',
  styleUrls: ['./magic-search-stem-eq.component.scss'],
  host: { '[class.ms-eq--horizontal]': 'horizontal' },
})
export class MagicSearchStemEqComponent {
  @Input() stems: stemsEQModel = { ...DEFAULT_STEMS, levels: { ...DEFAULT_STEMS.levels } };
  @Output() stemsChange = new EventEmitter<stemsEQModel>();
  @Input() eqEnabled: boolean = true;
  @Output() eqEnabledChange = new EventEmitter<boolean>();
  @Input() horizontal = false;

  stemIds: readonly StemId[] = MAGIC_SEARCH_STEM_IDS;
  presetNames = Object.keys(EQ_PRESETS);
  selectedPreset = 'Manual';
  eqMin = EQ_MIN;
  eqMax = EQ_MAX;

  getLevel(id: StemId): number { return this.stems.levels[id] ?? 0; }

  stemIcon(id: StemId): string { return STEM_ICONS[id] ?? 'pi-sliders-v'; }

  /** Returns inline style for the colored fill line on the track */
  getFillStyle(id: StemId): string {    const val = this.getLevel(id);
    const range = EQ_MAX - EQ_MIN;               // 24
    // slider thumb position as % from top (EQ_MAX = top, EQ_MIN = bottom)
    const zeroPos = ((EQ_MAX - 0) / range) * 100;   // 50%
    const valPos  = ((EQ_MAX - val) / range) * 100;

    if (val > 0) {
      // boost: fill from valPos up to zeroPos (upward, purple)
      return `top:${valPos}%;height:${zeroPos - valPos}%;background:linear-gradient(to bottom,#8B5CF6,#60a5fa);`;
    } else if (val < 0) {
      // cut/mute: fill from zeroPos down to valPos (pink/red)
      return `top:${zeroPos}%;height:${valPos - zeroPos}%;background:linear-gradient(to bottom,#f472b6,#ef4444);`;
    }
    return 'height:0';
  }

  /** Horizontal variant — left/width coordinates for the mobile sheet */
  getHFillStyle(id: StemId): string {
    const val = this.getLevel(id);
    const range = EQ_MAX - EQ_MIN;
    const zeroPos = ((0 - EQ_MIN) / range) * 100;
    const valPos  = ((val - EQ_MIN) / range) * 100;
    if (val > 0) {
      return `left:${zeroPos}%;width:${valPos - zeroPos}%;background:linear-gradient(to right,#8B5CF6,#60a5fa);`;
    } else if (val < 0) {
      return `left:${valPos}%;width:${zeroPos - valPos}%;background:linear-gradient(to right,#f472b6,#ef4444);`;
    }
    return 'width:0';
  }

  onWheel(id: StemId, e: WheelEvent): void {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1 : -1;
    const next = Math.max(EQ_MIN, Math.min(EQ_MAX, this.getLevel(id) + delta));
    this.onSliderChange(id, String(next));
  }

  onSliderChange(id: StemId, val: string): void {
    const levels = { ...this.stems.levels, [id]: +val };
    const muted = Object.entries(levels).filter(([, v]) => v <= EQ_MIN).map(([k]) => k);
    this.selectedPreset = 'Manual';
    this.stemsChange.emit({ levels: levels as stemsEQModel['levels'], muted: muted.length ? muted : null });
  }

  applyPreset(name: string): void {
    this.selectedPreset = name;
    const preset = EQ_PRESETS[name];
    if (!preset) return;
    const levels = { ...DEFAULT_STEMS.levels, ...preset } as stemsEQModel['levels'];
    const muted = Object.entries(levels).filter(([, v]) => v <= EQ_MIN).map(([k]) => k);
    this.stemsChange.emit({ levels, muted: muted.length ? muted : null });
  }

  reset(): void {
    this.selectedPreset = 'Manual';
    this.stemsChange.emit({ ...DEFAULT_STEMS, levels: { ...DEFAULT_STEMS.levels } });
  }

  toggleEq(): void { this.eqEnabledChange.emit(!this.eqEnabled); }

  get activeCount(): number { return Object.values(this.stems.levels).filter(v => v !== 0).length; }

  formatVal(v: number): string {
    if (v <= EQ_MIN) return 'MUTE';
    return v > 0 ? `+${v}` : `${v}`;
  }
}
